import React, { useState } from 'react';
import { Tooltip } from 'antd';

/* ── Layout constants (SVG user units) ───────────────────────────────────────
   The chart scales via width="100%" so these define only the aspect ratio.
   ─────────────────────────────────────────────────────────────────────────── */
const VIEW_W = 240;
const VIEW_H = 150;
const PAD_L = 38; // room for y-axis labels
const PAD_R = 6;
const PAD_T = 10;
const PAD_B = 28; // room for x-axis labels + axis title
const PLOT_W = VIEW_W - PAD_L - PAD_R; // 196
const PLOT_H = VIEW_H - PAD_T - PAD_B; // 112

/* ── Scenario palette ────────────────────────────────────────────────────────
   Exported so FinancialRiskPanel can use the same colours in the legend.
   Chart-specific palette values — not mapped to CSS design tokens.
   ─────────────────────────────────────────────────────────────────────────── */

/**
 * Revenue line colours for [Low, Mid, High] price/yield scenarios.
 * Yellow = Low (risky/hardest), Green = Mid (expected), Blue = High (easiest).
 */
export const PRICE_LINE_COLORS = ['#eab308', '#22c55e', '#3b82f6'] as const;

/** Human labels for each scenario index. */
export const PRICE_LABELS = ['Low', 'Expected', 'High'] as const;

/* ── Helpers ─────────────────────────────────────────────────────────────── */

/** Compact format for axis tick labels (space-constrained). */
const formatDollar = (v: number): string => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
};

/** Precise format for tooltip values — matches FinancialRiskPanel's formatDollar. */
const formatDollarPrecise = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(2)}k`;
  return `${sign}$${abs.toFixed(2)}`;
};

/* ── Props ───────────────────────────────────────────────────────────────── */

export interface BreakEvenChartProps {
  /** Total committed cost (positive $). */
  totalSpend: number;
  /** Total production area across all inputs (ha). */
  totalRevenueArea: number;
  /** Area-weighted reference yields [Low, Mid, High] (yield unit / ha). */
  refYields: [number, number, number];
  /**
   * Area-weighted reference prices [Low, Mid, High] ($ per yield unit).
   * A zero price means that scenario has no data and its line is omitted.
   */
  refPrices: [number, number, number];
  /** Yield unit string displayed on the x-axis (e.g. "t"). */
  yieldUnit: string;
}

/**
 * SVG break-even chart for the Financial Risk right-panel tab.
 *
 * ── What is shown ──────────────────────────────────────────────────────────
 * X-axis: yield per ha (yield unit / ha).
 * Y-axis: total revenue or cost ($).
 *
 * Revenue lines (one per price scenario with price > 0):
 *   revenue(y) = price × y × totalRevenueArea
 *   → straight line from origin, slope = price × totalRevenueArea
 *
 * Cost line (horizontal, dashed, danger colour):
 *   y = totalSpend
 *
 * Break-even intersection dots — hover to show a dark tooltip (Ant Design
 * tooltip style) with the scenario label and break-even yield.
 *
 * Text sizes use CSS classes (text-xs) so they stay fixed in viewport pixels
 * and do not scale with the SVG viewBox when the panel is resized.
 *
 * The legend is rendered by the parent FinancialRiskPanel below the chart.
 */
const BreakEvenChart: React.FC<BreakEvenChartProps> = ({
  totalSpend,
  totalRevenueArea,
  refYields,
  refPrices,
  yieldUnit,
}) => {
  const [hoveredBE, setHoveredBE] = useState<number | null>(null);
  const [crosshair, setCrosshair] = useState<{
    svgX: number;
    svgY: number;
  } | null>(null);

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    const svgY = ((e.clientY - rect.top) / rect.height) * VIEW_H;
    if (
      svgX >= PAD_L &&
      svgX <= PAD_L + PLOT_W &&
      svgY >= PAD_T &&
      svgY <= PAD_T + PLOT_H
    ) {
      setCrosshair({ svgX, svgY });
    } else {
      setCrosshair(null);
    }
  };

  const hasRevenue = totalRevenueArea > 0 && refPrices.some((p) => p > 0);

  // Break-even yield per scenario — 0 when price is 0 (no data)
  const beYields = refPrices.map((p) =>
    p > 0 && totalRevenueArea > 0 ? totalSpend / (p * totalRevenueArea) : 0
  );

  // Break-even price per scenario — 0 when yield is 0 (no data)
  const bePrices = refYields.map((y) =>
    y > 0 && totalRevenueArea > 0 ? totalSpend / (y * totalRevenueArea) : 0
  );

  // X domain: cover worst-case BE and high reference yield
  const maxBeYield = Math.max(...beYields, 0);
  const xMax = Math.max(refYields[2] * 1.3, maxBeYield * 1.3, 1);

  // Y domain: cost line always sits at 38–48 % of plot height.
  // Raw upper bound covers the full revenue projection; then we cap it at
  // totalSpend / 0.38 so the cost line never drops below ~38 % height even
  // when revenue at xMax is many multiples of the cost. Revenue lines that
  // extend above yMax are clipped to the plot boundary (see revenue line code).
  const maxRevenueAtXMax = hasRevenue
    ? Math.max(...refPrices.map((p) => p * xMax * totalRevenueArea), 0)
    : 0;
  const rawYMax = Math.max(maxRevenueAtXMax, totalSpend * 2);
  const cappedYMax =
    totalSpend > 0 ? Math.min(rawYMax, totalSpend / 0.38) : rawYMax;
  const yMax = (cappedYMax > 0 ? cappedYMax : 1) * 1.05;

  // Coordinate mapping: domain → SVG pixel
  const toX = (y: number) => PAD_L + (y / xMax) * PLOT_W;
  const toY = (d: number) => PAD_T + PLOT_H - (d / yMax) * PLOT_H;

  const axisBottom = PAD_T + PLOT_H;
  const axisLeft = PAD_L;

  // Ticks (5 marks including 0 and max)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);
  const xTicks = [0, 1 / 3, 2 / 3, 1].map((f) => f * xMax);

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      aria-label="Break-even analysis chart"
      onMouseMove={handleSvgMouseMove}
      onMouseLeave={() => setCrosshair(null)}
    >
      {/* ── Horizontal grid lines ── */}
      {yTicks.slice(1).map((v, i) => (
        <line
          key={`ygrid-${i}`}
          x1={axisLeft}
          y1={toY(v)}
          x2={axisLeft + PLOT_W}
          y2={toY(v)}
          stroke="var(--color-argent-gray)"
          strokeWidth="0.5"
        />
      ))}

      {/* ── Vertical grid lines ── */}
      {xTicks.slice(1).map((v, i) => (
        <line
          key={`xgrid-${i}`}
          x1={toX(v)}
          y1={PAD_T}
          x2={toX(v)}
          y2={axisBottom}
          stroke="var(--color-argent-gray)"
          strokeWidth="0.5"
        />
      ))}

      {/* ── Y-axis ticks and labels ── */}
      {yTicks.slice(1).map((v, i) => (
        <g key={`ytick-${i}`}>
          <line
            x1={axisLeft - 3}
            y1={toY(v)}
            x2={axisLeft}
            y2={toY(v)}
            stroke="var(--color-argent-gray)"
            strokeWidth="0.75"
          />
          <text
            x={axisLeft - 5}
            y={toY(v)}
            textAnchor="end"
            dominantBaseline="middle"
            className="text-xs"
            fill="var(--color-secondary-dark)"
          >
            {formatDollar(v)}
          </text>
        </g>
      ))}

      {/* ── X-axis ticks and labels ── */}
      {xTicks.map((v, i) => (
        <g key={`xtick-${i}`}>
          <line
            x1={toX(v)}
            y1={axisBottom}
            x2={toX(v)}
            y2={axisBottom + 3}
            stroke="var(--color-argent-gray)"
            strokeWidth="0.75"
          />
          <text
            x={toX(v)}
            y={axisBottom + 9}
            textAnchor={i === xTicks.length - 1 ? 'end' : 'middle'}
            className="text-xs"
            fill="var(--color-secondary-dark)"
          >
            {v.toFixed(1)}
          </text>
        </g>
      ))}

      {/* X-axis title */}
      <text
        x={axisLeft + PLOT_W / 2}
        y={VIEW_H - 2}
        textAnchor="middle"
        className="text-xs"
        fill="var(--color-secondary-dark)"
      >
        {`Yield (${yieldUnit}/ha)`}
      </text>

      {/* ── Axes ── */}
      <line
        x1={axisLeft}
        y1={PAD_T}
        x2={axisLeft}
        y2={axisBottom}
        stroke="var(--color-argent-gray)"
        strokeWidth="1"
      />
      <line
        x1={axisLeft}
        y1={axisBottom}
        x2={axisLeft + PLOT_W}
        y2={axisBottom}
        stroke="var(--color-argent-gray)"
        strokeWidth="1"
      />

      {/* ── Revenue lines — one per non-zero price scenario ── */}
      {/* Lines are clipped at yMax so they never draw outside the plot area  */}
      {/* when a high price × yield projection would exceed the Y ceiling.    */}
      {hasRevenue &&
        refPrices.map((price, i) => {
          if (price <= 0) return null;
          const rawEndY = price * xMax * totalRevenueArea;
          const endX =
            rawEndY <= yMax ? xMax : yMax / (price * totalRevenueArea);
          const endY = Math.min(rawEndY, yMax);
          return (
            <line
              key={`rev-${i}`}
              x1={toX(0)}
              y1={toY(0)}
              x2={toX(endX)}
              y2={toY(endY)}
              stroke={PRICE_LINE_COLORS[i]}
              strokeWidth="1.5"
            />
          );
        })}

      {/* ── TVC cost line (horizontal dashed) ── */}
      {totalSpend > 0 && (
        <line
          x1={axisLeft}
          y1={toY(totalSpend)}
          x2={axisLeft + PLOT_W}
          y2={toY(totalSpend)}
          stroke="var(--color-danger)"
          strokeWidth="1.5"
          strokeDasharray="4 2"
        />
      )}

      {/* ── Break-even intersection dots (interactive) ── */}
      {hasRevenue &&
        beYields.map((beY, i) => {
          if (refPrices[i] <= 0 || beY <= 0 || beY > xMax) return null;
          const isHovered = hoveredBE === i;
          return (
            <Tooltip
              key={`be-${i}`}
              title={
                <div>
                  <div className="font-semibold">{PRICE_LABELS[i]} price</div>
                  <div>{`Break-Even Yield: ${beY.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${yieldUnit}/ha`}</div>
                  <div>{`Revenue = Cost: ${formatDollarPrecise(totalSpend)}`}</div>
                  {bePrices[i] > 0 && (
                    <div>{`Break-Even Price: ${formatDollarPrecise(bePrices[i])}/${yieldUnit}`}</div>
                  )}
                </div>
              }
            >
              <g
                onMouseEnter={() => setHoveredBE(i)}
                onMouseLeave={() => setHoveredBE(null)}
              >
                <circle
                  cx={toX(beY)}
                  cy={toY(totalSpend)}
                  r={isHovered ? 4.5 : 3}
                  fill={PRICE_LINE_COLORS[i]}
                  stroke="white"
                  strokeWidth="0.75"
                  className="cursor-pointer"
                />
                {/* Transparent larger hit area for easier hover */}
                <circle
                  cx={toX(beY)}
                  cy={toY(totalSpend)}
                  r={8}
                  fill="transparent"
                />
              </g>
            </Tooltip>
          );
        })}

      {/* ── Crosshair ── */}
      {crosshair && (() => {
        const dataX = ((crosshair.svgX - PAD_L) / PLOT_W) * xMax;
        const dataY = (1 - (crosshair.svgY - PAD_T) / PLOT_H) * yMax;

        // Position label to the right of cursor, or left when near the right edge
        const showRight = crosshair.svgX + 82 <= PAD_L + PLOT_W;
        const textX = showRight ? crosshair.svgX + 5 : crosshair.svgX - 5;
        const anchor = showRight ? 'start' : 'end';

        // Position below cursor, or above when near the bottom
        const showBelow = crosshair.svgY + 26 <= PAD_T + PLOT_H;
        const line1Y = showBelow ? crosshair.svgY + 12 : crosshair.svgY - 14;
        const line2Y = showBelow ? crosshair.svgY + 24 : crosshair.svgY - 2;

        return (
          <g pointerEvents="none">
            {/* Vertical rule */}
            <line
              x1={crosshair.svgX}
              y1={PAD_T}
              x2={crosshair.svgX}
              y2={PAD_T + PLOT_H}
              stroke="var(--color-secondary-dark)"
              strokeWidth="0.5"
              strokeDasharray="3 2"
            />
            {/* Horizontal rule */}
            <line
              x1={PAD_L}
              y1={crosshair.svgY}
              x2={PAD_L + PLOT_W}
              y2={crosshair.svgY}
              stroke="var(--color-secondary-dark)"
              strokeWidth="0.5"
              strokeDasharray="3 2"
            />
            {/* X label (yield) */}
            <text
              x={textX}
              y={line1Y}
              textAnchor={anchor}
              className="text-xs"
              fill="var(--color-secondary-dark)"
              stroke="white"
              strokeWidth="3"
              paintOrder="stroke"
            >
              {`${dataX.toFixed(2)} ${yieldUnit}/ha`}
            </text>
            {/* Y label ($) */}
            <text
              x={textX}
              y={line2Y}
              textAnchor={anchor}
              className="text-xs"
              fill="var(--color-secondary-dark)"
              stroke="white"
              strokeWidth="3"
              paintOrder="stroke"
            >
              {formatDollar(dataY)}
            </text>
          </g>
        );
      })()}
    </svg>
  );
};

export default BreakEvenChart;
